import type { CSSProperties } from 'react'
import type { TransitionSpeed } from '@/lib/transitions'

// Shared transition-preview primitives — used by the Transitions page (browse /
// publish) AND the transition picker modal in the playlist/layout create + edit
// flows, so the animated previews stay identical everywhere. A transition's
// `anim` key (see lib/transitions.ts) maps to the looping `.tr-*` classes below.

// Preview loop length per speed — drives the previews' animation-duration via
// the --tr-dur CSS var so picking a speed changes how fast the previews play,
// matching what the players do at that speed.
export const SPEED_PREVIEW_DURATION: Record<TransitionSpeed, string> = {
  slow:   '3.8s',
  normal: '2.6s',
  fast:   '1.3s',
}

// Looping keyframes for the previews. Each `.tr-*` class animates the incoming
// "B" layer over the static "A" layer, then loops. Assigned ONLY while a board
// is `.tr-playing`, the card is hovered / focused, so a stopped preview snaps
// back to its start state instead of freezing mid-animation.
export const TRANSITION_PREVIEW_CSS = `
.tr-stage { perspective: 500px; }
.tr-layer { position: absolute; inset: 0; display: grid; place-items: center; backface-visibility: hidden; }
.tr-in { opacity: 0; animation-duration: var(--tr-dur, 2.6s); animation-timing-function: ease-in-out; animation-iteration-count: infinite; will-change: transform, opacity, filter, clip-path; }
@keyframes k-fade   { 0%{opacity:0} 45%{opacity:1} 80%{opacity:1} 100%{opacity:0} }
@keyframes k-diss   { 0%{opacity:0;filter:blur(7px)} 45%{opacity:1;filter:blur(0)} 80%{opacity:1;filter:blur(0)} 100%{opacity:0;filter:blur(7px)} }
@keyframes k-sl-l   { 0%{transform:translateX(100%);opacity:1} 45%,80%{transform:translateX(0);opacity:1} 100%{transform:translateX(-100%);opacity:1} }
@keyframes k-sl-r   { 0%{transform:translateX(-100%);opacity:1} 45%,80%{transform:translateX(0);opacity:1} 100%{transform:translateX(100%);opacity:1} }
@keyframes k-sl-u   { 0%{transform:translateY(100%);opacity:1} 45%,80%{transform:translateY(0);opacity:1} 100%{transform:translateY(-100%);opacity:1} }
@keyframes k-sl-d   { 0%{transform:translateY(-100%);opacity:1} 45%,80%{transform:translateY(0);opacity:1} 100%{transform:translateY(100%);opacity:1} }
@keyframes k-zin    { 0%{transform:scale(.4);opacity:0} 45%,80%{transform:scale(1);opacity:1} 100%{transform:scale(1.6);opacity:0} }
@keyframes k-zout   { 0%{transform:scale(1.6);opacity:0} 45%,80%{transform:scale(1);opacity:1} 100%{transform:scale(.4);opacity:0} }
@keyframes k-zblur  { 0%{transform:scale(1.4);opacity:0;filter:blur(9px)} 45%,80%{transform:scale(1);opacity:1;filter:blur(0)} 100%{transform:scale(.9);opacity:0;filter:blur(9px)} }
@keyframes k-flip-h { 0%{transform:rotateY(90deg);opacity:0} 45%,80%{transform:rotateY(0);opacity:1} 100%{transform:rotateY(-90deg);opacity:0} }
@keyframes k-flip-v { 0%{transform:rotateX(90deg);opacity:0} 45%,80%{transform:rotateX(0);opacity:1} 100%{transform:rotateX(-90deg);opacity:0} }
@keyframes k-rot    { 0%{transform:rotate(-25deg) scale(.5);opacity:0} 45%,80%{transform:rotate(0) scale(1);opacity:1} 100%{transform:rotate(25deg) scale(.5);opacity:0} }
@keyframes k-spin   { 0%{transform:rotate(0) scale(.4);opacity:0} 45%{transform:rotate(360deg) scale(1);opacity:1} 80%{transform:rotate(360deg) scale(1);opacity:1} 100%{transform:rotate(720deg) scale(.4);opacity:0} }
@keyframes k-wp-l   { 0%{clip-path:inset(0 0 0 100%);opacity:1} 45%,80%{clip-path:inset(0);opacity:1} 100%{clip-path:inset(0 100% 0 0);opacity:1} }
@keyframes k-wp-u   { 0%{clip-path:inset(100% 0 0 0);opacity:1} 45%,80%{clip-path:inset(0);opacity:1} 100%{clip-path:inset(0 0 100% 0);opacity:1} }
@keyframes k-cube   { 0%{transform:rotateY(90deg) translateZ(24px);opacity:0} 45%,80%{transform:rotateY(0) translateZ(0);opacity:1} 100%{transform:rotateY(-90deg) translateZ(24px);opacity:0} }
@keyframes k-cut    { 0%,49%{opacity:0} 50%,100%{opacity:1} }
.tr-playing .tr-cut,         .group:hover .tr-cut,         .group:focus-within .tr-cut         { animation-name: k-cut; animation-timing-function: steps(1); }
.tr-playing .tr-fade,        .group:hover .tr-fade,        .group:focus-within .tr-fade        { animation-name: k-fade; }
.tr-playing .tr-dissolve,    .group:hover .tr-dissolve,    .group:focus-within .tr-dissolve    { animation-name: k-diss; }
.tr-playing .tr-slide-left,  .group:hover .tr-slide-left,  .group:focus-within .tr-slide-left  { animation-name: k-sl-l; }
.tr-playing .tr-slide-right, .group:hover .tr-slide-right, .group:focus-within .tr-slide-right { animation-name: k-sl-r; }
.tr-playing .tr-slide-up,    .group:hover .tr-slide-up,    .group:focus-within .tr-slide-up    { animation-name: k-sl-u; }
.tr-playing .tr-slide-down,  .group:hover .tr-slide-down,  .group:focus-within .tr-slide-down  { animation-name: k-sl-d; }
.tr-playing .tr-zoom-in,     .group:hover .tr-zoom-in,     .group:focus-within .tr-zoom-in     { animation-name: k-zin; }
.tr-playing .tr-zoom-out,    .group:hover .tr-zoom-out,    .group:focus-within .tr-zoom-out    { animation-name: k-zout; }
.tr-playing .tr-zoom-blur,   .group:hover .tr-zoom-blur,   .group:focus-within .tr-zoom-blur   { animation-name: k-zblur; }
.tr-playing .tr-flip-h,      .group:hover .tr-flip-h,      .group:focus-within .tr-flip-h      { animation-name: k-flip-h; }
.tr-playing .tr-flip-v,      .group:hover .tr-flip-v,      .group:focus-within .tr-flip-v      { animation-name: k-flip-v; }
.tr-playing .tr-rotate,      .group:hover .tr-rotate,      .group:focus-within .tr-rotate      { animation-name: k-rot; }
.tr-playing .tr-spin,        .group:hover .tr-spin,        .group:focus-within .tr-spin        { animation-name: k-spin; }
.tr-playing .tr-wipe-left,   .group:hover .tr-wipe-left,   .group:focus-within .tr-wipe-left   { animation-name: k-wp-l; }
.tr-playing .tr-wipe-up,     .group:hover .tr-wipe-up,     .group:focus-within .tr-wipe-up     { animation-name: k-wp-u; }
.tr-playing .tr-cube,        .group:hover .tr-cube,        .group:focus-within .tr-cube        { animation-name: k-cube; }
@media (prefers-reduced-motion: reduce){ .tr-in { animation: none; opacity: 1; } }
`

// Injects the preview keyframes once. Render a single instance high in the tree
// that shows previews (the Transitions page or the picker modal).
export function TransitionPreviewStyles() {
  return <style>{TRANSITION_PREVIEW_CSS}</style>
}

// The two-slide preview stage for one transition. Outgoing slide (Frame mark on
// the surface colour) sits under the incoming brand slide (inverted mark) that
// animates in via the transition's `anim` class. `logoClass` sizes the mark.
export function TransitionPreview({
  anim, className = 'h-24', logoClass = 'h-7',
}: {
  anim: string
  className?: string
  logoClass?: string
}) {
  return (
    <div className={`tr-stage relative overflow-hidden bg-white dark:bg-neutral-900 ${className}`}>
      <div className="tr-layer bg-white dark:bg-neutral-900">
        {/* Outgoing slide: dark mark on the white surface (light) / white mark on
            the dark surface (dark) so it stays legible in both themes. */}
        <img src="/frame-logo.avif" alt="" className={`${logoClass} dark:brightness-0 dark:invert`} />
      </div>
      <div className={`tr-layer tr-in ${anim} bg-blue-500 dark:bg-primary`}>
        {/* Incoming slide. In LIGHT mode it is Frame blue with a white mark:
            bg-primary is brand black here, so the preview was a black card
            sliding onto a white one — two dark-on-light slabs that read as a
            glitch rather than a transition. blue-500 is exactly #165CFC (see
            tailwind.brandBlue.js), the logo's own blue.
            Dark mode is unchanged: bg-primary is near-white there, which
            already contrasts against the neutral-900 outgoing slide. */}
        <img src="/frame-logo.avif" alt="" className={`${logoClass} brightness-0 invert dark:invert-0`} />
      </div>
    </div>
  )
}

// Convenience: the --tr-dur inline style for a given speed.
export function speedDurationStyle(speed: TransitionSpeed): CSSProperties {
  return { '--tr-dur': SPEED_PREVIEW_DURATION[speed] } as CSSProperties
}
